import React from "react";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyIcon from "@mui/icons-material/Key";
import SaveIcon from "@mui/icons-material/Save";

import { rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";
import { can, canAll } from "@src/security/permissions";

import RolePermissionSelector, {
  permissionNames,
  selectedPermissionNames,
  samePermissionSelection
} from "@src/users/RolePermissionSelector";

const request = async (
  path,
  method = "GET",
  body = null
) => {
  return await rest.request(
    getServiceUri() + path,
    method,
    body
  );
};

const generateApiKey = () => {
  const bytes = new Uint8Array(32);

  window.crypto.getRandomValues(bytes);

  let binary = "";

  for (const value of bytes) {
    binary += String.fromCharCode(value);
  }

  const encoded =
    window.btoa(binary)
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/g, "");

  return `cep_${encoded}`;
};

const ApiUserFormView = ({
  gui,
  id
}) => {
  const [data, setData] =
    React.useState(null);

  const [accessProfile, setAccessProfile] =
    React.useState(null);

  const [selectedPermissions, setSelectedPermissions] =
    React.useState(new Set());

  const [initialPermissions, setInitialPermissions] =
    React.useState(new Set());

  const [description, setDescription] =
    React.useState("");

  const [loading, setLoading] =
    React.useState(true);

  const [savingDetails, setSavingDetails] =
    React.useState(false);

  const [savingPermissions, setSavingPermissions] =
    React.useState(false);

  const [generating, setGenerating] =
    React.useState(false);

  const [error, setError] =
    React.useState("");

  const [accessError, setAccessError] =
    React.useState("");

  const [permissionsSaved, setPermissionsSaved] =
    React.useState(false);

  const [generatedKey, setGeneratedKey] =
    React.useState("");

  const [keyDialogOpen, setKeyDialogOpen] =
    React.useState(false);

  const [copied, setCopied] =
    React.useState(false);

  const canUpdate =
    can(gui, "UPDATE_API_USERS");

  const canManageAccess =
    can(gui, "MANAGE_API_USER_ACCESS");

  const canReturnToList =
    canAll(gui, [
      "SEARCH_API_USERS",
      "COUNT_API_USERS"
    ]);

  const applyAccessProfile =
    React.useCallback(
      (profile) => {
        const selected =
          selectedPermissionNames(profile);

        setAccessProfile(profile);
        setSelectedPermissions(selected);
        setInitialPermissions(
          new Set(selected)
        );
      },
      []
    );

  const load = React.useCallback(
    async () => {
      setLoading(true);
      setError("");
      setAccessError("");

      try {
        const profile =
          await request(
            `__api-users/${Number(id)}/access`
          );

        setData(profile.user);
        setDescription(
          String(
            profile?.user?.description || ""
          )
        );
        applyAccessProfile(profile);

      } catch (loadError) {
        console.error(
          "API user loading failed",
          loadError
        );

        setError(
          t("user.unableLoadApiUser")
        );

      } finally {
        setLoading(false);
      }
    },
    [applyAccessProfile, id]
  );

  React.useEffect(
    () => {
      load();
    },
    [load]
  );

  const saveDescription = async () => {
    if (!canUpdate || savingDetails) {
      return;
    }

    setSavingDetails(true);
    setError("");

    try {
      await request(
        "__api-users",
        "PUT",
        {
          id: Number(id),
          description
        }
      );

      setData((current) => ({
        ...current,
        description
      }));

    } catch (saveError) {
      console.error(
        "API user update failed",
        saveError
      );

      setError(
        t("user.unableSaveApiUser")
      );

    } finally {
      setSavingDetails(false);
    }
  };

  const toggleRoleSet = (roleSet) => {
    if (!canManageAccess) {
      return;
    }

    const names = permissionNames(roleSet);

    setPermissionsSaved(false);

    setSelectedPermissions(
      (current) => {
        const next = new Set(current);

        if (names.includes("ALL")) {
          if (next.has("ALL")) {
            next.delete("ALL");
          } else {
            next.clear();
            next.add("ALL");
          }

          return next;
        }

        next.delete("ALL");

        const fullySelected =
          names.length > 0
          && names.every(
            (name) => next.has(name)
          );

        for (const name of names) {
          if (fullySelected) {
            next.delete(name);
          } else {
            next.add(name);
          }
        }

        return next;
      }
    );
  };

  const togglePermission = (permissionName) => {
    if (!canManageAccess) {
      return;
    }

    setPermissionsSaved(false);

    setSelectedPermissions(
      (current) => {
        const next = new Set(current);

        if (permissionName === "ALL") {
          if (next.has("ALL")) {
            next.delete("ALL");
          } else {
            next.clear();
            next.add("ALL");
          }

          return next;
        }

        next.delete("ALL");

        if (next.has(permissionName)) {
          next.delete(permissionName);
        } else {
          next.add(permissionName);
        }

        return next;
      }
    );
  };

  const savePermissions = async () => {
    if (!canManageAccess || savingPermissions) {
      return;
    }

    setSavingPermissions(true);
    setAccessError("");
    setPermissionsSaved(false);

    try {
      const profile =
        await request(
          `__api-users/${Number(id)}/access`,
          "PUT",
          {
            permissions:
              Array.from(
                selectedPermissions
              )
          }
        );

      applyAccessProfile(profile);
      setPermissionsSaved(true);

    } catch (saveError) {
      console.error(
        "API user permission update failed",
        saveError
      );

      setAccessError(
        t("user.roleAssignmentsCouldNotBeSaved")
      );

    } finally {
      setSavingPermissions(false);
    }
  };

  const regenerateKey = async () => {
    if (!canUpdate || generating) {
      return;
    }

    setGenerating(true);
    setError("");
    setCopied(false);

    const apiKey = generateApiKey();

    try {
      await request(
        "__api-users",
        "PUT",
        {
          id: Number(id),
          apiKey
        }
      );

      setGeneratedKey(apiKey);
      setKeyDialogOpen(true);

    } catch (generateError) {
      console.error(
        "API key generation failed",
        generateError
      );

      setGeneratedKey("");
      setError(
        t("user.unableGenerateApiKey")
      );

    } finally {
      setGenerating(false);
    }
  };

  const copyKey = async () => {
    if (!generatedKey) {
      return;
    }

    await navigator.clipboard.writeText(
      generatedKey
    );

    setCopied(true);
  };

  const closeKeyDialog = () => {
    setKeyDialogOpen(false);
    setGeneratedKey("");
    setCopied(false);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          p: 6
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!data || !accessProfile) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error || t("user.apiUserNotFound")}
        </Alert>
      </Box>
    );
  }

  const permissionsDirty =
    !samePermissionSelection(
      selectedPermissions,
      initialPermissions
    );

  return (
    <Box
      sx={{
        maxWidth: 1120,
        mx: "auto",
        p: 3
      }}
    >
      <Stack spacing={3}>
        <Box>
          <Typography
            variant="h4"
            component="h1"
          >
            {t("user.apiUser")}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
          >
            ID {data.id}
          </Typography>
        </Box>

        {error ? (
          <Alert severity="error">
            {error}
          </Alert>
        ) : null}

        <Paper
          variant="outlined"
          sx={{ p: 3 }}
        >
          <Stack spacing={3}>
            <Typography variant="h6">
              {t("common.details")}
            </Typography>

            <TextField
              label={t("common.id")}
              value={data.id}
              disabled
              fullWidth
            />

            <TextField
              label={t("common.description")}
              value={description}
              onChange={(event) => {
                setDescription(
                  event.target.value
                );
              }}
              disabled={!canUpdate}
              fullWidth
            />

            {canUpdate ? (
              <Box>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={savingDetails}
                  onClick={saveDescription}
                >
                  {savingDetails
                    ? t("common.saving")
                    : t("common.save")}
                </Button>
              </Box>
            ) : null}
          </Stack>
        </Paper>

        <Paper
          variant="outlined"
          sx={{ p: 3 }}
        >
          <Stack spacing={2}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 2
              }}
            >
              <Box>
                <Typography variant="h6">
                  {t("user.rolesAndPermissions")}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {t("user.selectFunctionalAccessGranular")}
                </Typography>
              </Box>

              {canManageAccess ? (
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={
                    savingPermissions
                    || !permissionsDirty
                  }
                  onClick={savePermissions}
                >
                  {savingPermissions
                    ? t("common.saving")
                    : t("user.savePermissions")}
                </Button>
              ) : null}
            </Box>

            <Divider />

            <RolePermissionSelector
              roleSets={accessProfile.roleSets || []}
              selectedPermissions={selectedPermissions}
              saving={savingPermissions || !canManageAccess}
              onToggleRole={toggleRoleSet}
              onTogglePermission={togglePermission}
            />

            {accessError ? (
              <Alert severity="error">
                {accessError}
              </Alert>
            ) : null}

            {permissionsSaved ? (
              <Alert severity="success">
                {t("user.permissionAssignmentsSaved")}
              </Alert>
            ) : null}
          </Stack>
        </Paper>

        <Paper
          variant="outlined"
          sx={{ p: 3 }}
        >
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6">
                {t("user.apiKey")}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                {t("user.apiKeyCurrentCannotBeViewed")}
              </Typography>
            </Box>

            <Divider />

            {canUpdate ? (
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<KeyIcon />}
                  disabled={generating}
                  onClick={regenerateKey}
                >
                  {generating
                    ? t("common.generating")
                    : t("user.generateApiKey")}
                </Button>
              </Box>
            ) : null}
          </Stack>
        </Paper>

        {canReturnToList ? (
          <Box>
            <Button
              onClick={() => {
                gui.goTo("/api-user");
              }}
            >
              {t("user.backToApiUsers")}
            </Button>
          </Box>
        ) : null}
      </Stack>

      <Dialog
        open={keyDialogOpen}
        onClose={closeKeyDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {t("user.newApiKey")}
        </DialogTitle>

        <DialogContent>
          <Stack
            spacing={2}
            sx={{ pt: 1 }}
          >
            <Alert severity="warning">
              {t("user.copyApiKeyNow")}
            </Alert>

            <TextField
              label={t("user.apiKey")}
              value={generatedKey}
              fullWidth
              multiline
              minRows={2}
              InputProps={{
                readOnly: true
              }}
            />

            {copied ? (
              <Alert severity="success">
                {t("user.apiKeyCopied")}
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            startIcon={<ContentCopyIcon />}
            onClick={copyKey}
          >
            {t("common.copy")}
          </Button>

          <Button
            variant="contained"
            onClick={closeKeyDialog}
          >
            {t("common.done")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const displayReadApiUser = (
  gui,
  id
) => {
  return {
    uuid: `api-user-read-${id}`,

    view: () => (
      <ApiUserFormView
        gui={gui}
        id={id}
      />
    )
  };
};

export const displayEditApiUser =
  displayReadApiUser;

export const displayApiUserForm =
  displayReadApiUser;

export default displayReadApiUser;
